// AssetValuationDetailsModal.jsx
import React, { useState, useEffect } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
} from "reactstrap";
import PreliminaryDetailsTab from "./Tabs/PreliminaryDetailsTab";
import BuildingsTab from "./Tabs/BuildingsTab";
import EquipmentTab from "./Tabs/EquipmentTab";
import FurnitureTab from "./Tabs/FurnitureTab";
import ICTTab from "./Tabs/ICTTab";
import LandTab from "./Tabs/LandTab";
import MachineryTab from "./Tabs/MachineryTab";
import VehiclesTab from "./Tabs/VehiclesTab";
import SiteWorksTab from "./Tabs/SiteWorksTab";
import { getAssetsByValuation } from "../../../services/ValitanApiService";
import Pagination from "../../../components/Pagination/Pagination";

const BRAND_BLUE = "#0147C8";
const DEFAULT_PAGE_SIZE = 10;

const TAB_KEYS = [
    "buildings",
    "equipment",
    "furniture",
    "ict",
    "land",
    "machinery",
    "vehicles",
    "siteworks",
];

const makeInitialPaginationByTab = () =>
    TAB_KEYS.reduce((acc, key) => {
        acc[key] = { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
        return acc;
    }, {});

const AssetValuationDetailsModal = ({ isOpen, toggle, valuation }) => {
    const [activeTab, setActiveTab] = useState("preliminary");
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationByTab, setPaginationByTab] = useState(makeInitialPaginationByTab);

    useEffect(() => {
        if (!isOpen || !valuation?.id) return;

        const fetchAssets = async () => {
            setLoading(true);
            try {
                const res = await getAssetsByValuation(valuation.id);
                setAssets(res.data?.data || res.data || []);
            } catch (err) {
                console.error("Failed to load assets:", err);
                setAssets([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets();
    }, [isOpen, valuation?.id]);

    // When a new valuation is opened (or assets refresh), reset pagination to page 1 for all tabs.
    useEffect(() => {
        if (!isOpen) return;
        setPaginationByTab(makeInitialPaginationByTab());
    }, [isOpen, valuation?.id]);

    const filterByCategory = (code) => {
        return assets.filter((a) => a.asset_category?.code === code);
    };

    const getPaginatedAssets = (tabKey, categoryCode) => {
        const full = filterByCategory(categoryCode);
        const { pageNumber, pageSize } = paginationByTab?.[tabKey] || {
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        };
        const start = pageNumber * pageSize;
        return {
            full,
            paged: full.slice(start, start + pageSize),
            pageNumber,
            pageSize,
            totalElements: full.length,
            totalPages: Math.max(1, Math.ceil(full.length / pageSize)),
        };
    };

    const onPageChangeFor = (tabKey) => (pageNumber, pageSize) => {
        setPaginationByTab((prev) => ({
            ...prev,
            [tabKey]: { pageNumber, pageSize },
        }));
    };

    const onPageSizeChangeFor = (tabKey) => (pageSize) => {
        setPaginationByTab((prev) => ({
            ...prev,
            [tabKey]: { pageNumber: 0, pageSize },
        }));
    };

    const handlePreviousPageFor = (tabKey, totalPages) => () => {
        setPaginationByTab((prev) => {
            const current = prev?.[tabKey] || { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
            return {
                ...prev,
                [tabKey]: { ...current, pageNumber: Math.max(0, current.pageNumber - 1) },
            };
        });
    };

    const handleNextPageFor = (tabKey, totalPages) => () => {
        setPaginationByTab((prev) => {
            const current = prev?.[tabKey] || { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
            return {
                ...prev,
                [tabKey]: {
                    ...current,
                    pageNumber: Math.min(totalPages - 1, current.pageNumber + 1),
                },
            };
        });
    };

    const renderCategoryTab = (tabKey, categoryCode, TabComponent) => {
        const { paged, pageNumber, pageSize, totalElements, totalPages } = getPaginatedAssets(
            tabKey,
            categoryCode
        );

        return (
            <>
                <TabComponent assets={paged} />
                {totalElements > 0 && (
                    <div className="mt-3">
                        <Pagination
                            paginationInfo={{ pageNumber, pageSize, totalPages, totalElements }}
                            handleNextPage={handleNextPageFor(tabKey, totalPages)}
                            handlePreviousPage={handlePreviousPageFor(tabKey, totalPages)}
                            onPageChange={onPageChangeFor(tabKey)}
                            onPageSizeChange={onPageSizeChangeFor(tabKey)}
                        />
                    </div>
                )}
            </>
        );
    };

    // Clean, minimal tab style
    const tabStyle = (isActive) => ({
        padding: "10px 16px",
        margin: "0 6px",
        borderRadius: "50px",
        fontWeight: "600",
        fontSize: "13px",
        color: isActive ? "white" : "#333",
        backgroundColor: isActive ? BRAND_BLUE : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "all 0.25s ease",
        whiteSpace: "nowrap",
    });

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" keyboard={false}>
            <ModalHeader toggle={toggle} className="border-0 pb-0">
                <h5 className="mb-0 fw-bold" style={{ color: BRAND_BLUE }}>
                    Valuation — {valuation?.valuation_ref || "N/A"}
                </h5>
            </ModalHeader>

            <ModalBody className="pt-3 px-4">
                {/* TABS — Clean & Minimal */}
                <Nav tabs className="flex-wrap justify-content-start gap-2 mb-4 border-bottom pb-3">
                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "preliminary")}
                            onClick={() => setActiveTab("preliminary")}
                        >
                            Details
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "buildings")}
                            onClick={() => setActiveTab("buildings")}
                        >
                            Buildings
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "equipment")}
                            onClick={() => setActiveTab("equipment")}
                        >
                            Equipment
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "furniture")}
                            onClick={() => setActiveTab("furniture")}
                        >
                            Furniture
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "ict")}
                            onClick={() => setActiveTab("ict")}
                        >
                            ICT
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "land")}
                            onClick={() => setActiveTab("land")}
                        >
                            Land
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "machinery")}
                            onClick={() => setActiveTab("machinery")}
                        >
                            Machinery
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "vehicles")}
                            onClick={() => setActiveTab("vehicles")}
                        >
                            Vehicles
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "siteworks")}
                            onClick={() => setActiveTab("siteworks")}
                        >
                            Site Works
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* TAB CONTENT */}
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="preliminary">
                        <PreliminaryDetailsTab valuation={valuation} />
                    </TabPane>

                    {loading ? (
                        <TabPane tabId="loading">
                            <div className="text-center py-5">
                                <Spinner style={{ width: "3.5rem", height: "3.5rem", color: BRAND_BLUE }} />
                                <p className="mt-3 text-muted">Loading assets...</p>
                            </div>
                        </TabPane>
                    ) : (
                        <>
                            <TabPane tabId="buildings">
                                {renderCategoryTab("buildings", "BLDG", BuildingsTab)}
                            </TabPane>
                            <TabPane tabId="equipment">
                                {renderCategoryTab("equipment", "EQP", EquipmentTab)}
                            </TabPane>
                            <TabPane tabId="furniture">
                                {renderCategoryTab("furniture", "FURN", FurnitureTab)}
                            </TabPane>
                            <TabPane tabId="ict">
                                {renderCategoryTab("ict", "ICT", ICTTab)}
                            </TabPane>
                            <TabPane tabId="land">
                                {renderCategoryTab("land", "LAND", LandTab)}
                            </TabPane>
                            <TabPane tabId="machinery">
                                {renderCategoryTab("machinery", "MACH", MachineryTab)}
                            </TabPane>
                            <TabPane tabId="vehicles">
                                {renderCategoryTab("vehicles", "VEH", VehiclesTab)}
                            </TabPane>
                            <TabPane tabId="siteworks">
                                {renderCategoryTab("siteworks", "SITE", SiteWorksTab)}
                            </TabPane>
                        </>
                    )}
                </TabContent>
            </ModalBody>

            <ModalFooter className="border-0 justify-content-center">
                <Button
                    onClick={toggle}
                    style={{
                        backgroundColor: BRAND_BLUE,
                        border: "none",
                        borderRadius: "50px",
                        padding: "12px 40px",
                        fontWeight: "600",
                        fontSize: "15px",
                    }}
                >
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AssetValuationDetailsModal;